import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { cget, cset } from "@/lib/cached";
import ProductCard from "@/components/ProductCard";
import SkinQuiz from "@/components/SkinQuiz";
import { ArrowRight, Leaf, FlaskConical, Sparkles, Shield } from "lucide-react";

const BENEFITS = [
  { icon: FlaskConical, title: "Clinically inspired", body: "Formulated with dermatologists using proven actives — PDRN, Redensyl, Niacinamide." },
  { icon: Leaf, title: "Sulphate & paraben free", body: "Botanically enriched, gentle to skin and scalp — cruelty-free and vegan-friendly." },
  { icon: Shield, title: "Made in India", body: "ISO & GMP certified manufacturing. Imported actives from Switzerland, Italy & USA." },
  { icon: Sparkles, title: "Consistency, made easy", body: "Subscribe to skincare that arrives when you need it — never a jar-scrape moment." },
];

export default function Home() {
  const [products, setProducts] = useState(() => cget("imu_products") || []);
  const [banners, setBanners] = useState(() => cget("imu_banners") || []);
  const [idx, setIdx] = useState(0);
  // siteImages: null = never loaded (show skeleton); {} = loaded but empty
  const [siteImages, setSiteImages] = useState(() => cget("imu_siteImages"));
  const [heroIdx, setHeroIdx] = useState(0);
  const [copy, setCopy] = useState(() => cget("imu_copy") || {});

  useEffect(() => {
    api.get("/products").then((r) => { setProducts(r.data); cset("imu_products", r.data); });
    api.get("/banners").then((r) => { setBanners(r.data); cset("imu_banners", r.data); });
    api.get("/settings/site-images").then((r) => { setSiteImages(r.data); cset("imu_siteImages", r.data); }).catch(() => setSiteImages({}));
    api.get("/settings/copy").then((r) => { setCopy(r.data); cset("imu_copy", r.data); }).catch(() => {});
  }, []);
  const t = (k, fallback = "") => copy[k] ?? fallback;

  const heroList = siteImages
    ? ((siteImages.hero_images && siteImages.hero_images.length > 0)
        ? siteImages.hero_images
        : (siteImages.hero_image ? [siteImages.hero_image] : []))
    : [];
  const storyImg = siteImages?.story_image || "";

  useEffect(() => {
    if (heroList.length < 2) return;
    const t2 = setInterval(() => setHeroIdx((i) => (i + 1) % heroList.length), 5000);
    return () => clearInterval(t2);
  }, [heroList.length]);

  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 5500);
    return () => clearInterval(t);
  }, [banners.length]);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 lg:pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 flex flex-col justify-center animate-fade-in-up">
            <div className="eyebrow" data-testid="hero-eyebrow">{t("hero_eyebrow", "The essentials — 2026")}</div>
            <h1 className="mt-6 font-display text-[52px] lg:text-[76px] leading-[0.96] tracking-[-0.02em] text-ink-900" data-testid="hero-title">
              {t("hero_title_1", "Skincare,")} <br />
              <em className="serif-accent text-gold-500">{t("hero_title_accent", "quietly")}</em> {t("hero_title_2", "radical.")}
            </h1>
            <p className="mt-8 text-[15px] leading-relaxed text-ink-700 max-w-md" data-testid="hero-body">
              {t("hero_body", "Four essentials — sun, serum, scalp and cleanse — engineered with clinically-inspired actives and botanically-rich extracts. For skin that behaves and hair that listens.")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" className="btn-primary-ink px-8 py-4 text-xs uppercase tracking-[0.28em] inline-flex items-center gap-2" data-testid="hero-shop-btn">
                {t("hero_cta_primary", "Shop now")} <ArrowRight size={16} />
              </Link>
              <Link to="/dermatologists" className="btn-gold-outline px-8 py-4 text-xs uppercase tracking-[0.28em] inline-flex items-center gap-2" data-testid="hero-derm-btn">
                {t("hero_cta_secondary", "Talk to a Dermatologist")}
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-8 text-[11px] uppercase tracking-widest text-ink-500">
              <span>ISO Certified</span>
              <span className="hairline border-l h-3" />
              <span>Cruelty Free</span>
              <span className="hairline border-l h-3" />
              <span>Vegan-friendly</span>
            </div>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="relative aspect-[5/4] bg-cream-200 overflow-hidden">
              {siteImages === null && (
                <div className="absolute inset-0 bg-cream-200 animate-pulse" aria-hidden="true" data-testid="hero-skeleton" />
              )}
              {heroList.map((src, i) => (
                <img
                  key={src + i}
                  src={src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src}
                  alt="IMMUSKI product collection"
                  loading={i === 0 ? "eager" : "lazy"}
                  fetchPriority={i === 0 ? "high" : "auto"}
                  decoding="async"
                  className={"absolute inset-0 w-full h-full object-cover transition-opacity duration-700 " + (i === heroIdx ? "opacity-100" : "opacity-0")}
                  data-testid={i === 0 ? "home-hero-image" : `home-hero-image-${i}`}
                />
              ))}
              {heroList.length > 1 && (
                <div className="absolute top-4 right-4 flex gap-2 z-10">
                  {heroList.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setHeroIdx(i)}
                      data-testid={`hero-dot-${i}`}
                      aria-label={`Hero image ${i + 1}`}
                      className={"h-[3px] w-8 transition-colors " + (i === heroIdx ? "bg-ink-900" : "bg-white/70")}
                    />
                  ))}
                </div>
              )}
              <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                <div className="bg-cream-100/85 backdrop-blur px-4 py-3">
                  <div className="eyebrow">{t("hero_badge_featured_label", "Featured")}</div>
                  <div className="font-display text-lg text-ink-900">{t("hero_badge_featured_text", "The Four")}</div>
                </div>
                <div className="bg-ink-900 text-cream-100 px-4 py-3">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-gold-400">{t("hero_badge_from_label", "From")}</div>
                  <div className="font-display text-lg">{t("hero_badge_from_price", "₹799")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROMO BANNER CAROUSEL */}
      {banners.length > 0 && (
        <section className="border-y hairline bg-cream-200 relative">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="relative aspect-[16/9] overflow-hidden bg-ink-900">
              {banners.map((b, i) => (
                <img key={b.id} src={b.image_url} alt={b.headline} loading="lazy"
                  className={"absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 " + (i === idx ? "opacity-100" : "opacity-0")} />
              ))}
            </div>
            <div>
              <div className="eyebrow">Now</div>
              <h2 className="mt-3 font-display text-4xl text-ink-900">{banners[idx]?.headline}</h2>
              <p className="mt-4 text-ink-700 max-w-md">{banners[idx]?.subhead}</p>
              {banners[idx]?.cta_link && (
                <Link to={banners[idx].cta_link} className="mt-6 inline-flex items-center gap-2 link-underline text-sm">
                  {banners[idx]?.cta_label || "Explore"} <ArrowRight size={14} />
                </Link>
              )}
              <div className="mt-6 flex gap-2">
                {banners.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)} data-testid={`banner-dot-${i}`}
                    className={"h-[2px] w-8 " + (i === idx ? "bg-ink-900" : "bg-ink-300")} aria-label={`Slide ${i+1}`} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PRODUCT GRID */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-24">
        <div className="flex items-end justify-between mb-8 sm:mb-12">
          <div>
            <div className="eyebrow">{t("collection_eyebrow", "The collection")}</div>
            <h2 className="mt-3 font-display text-3xl sm:text-5xl text-ink-900">{t("collection_title", "Four essentials.")}</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-sm link-underline">Shop all <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-10">
          {products.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-ink-900 text-cream-100 relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-16 sm:py-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
          {BENEFITS.map((b, i) => (
            <div key={i} className="border-t border-gold-500/40 pt-6">
              <b.icon size={22} className="text-gold-400" />
              <h3 className="mt-4 sm:mt-6 font-display text-[20px] sm:text-[24px] leading-tight">{b.title}</h3>
              <p className="mt-3 text-sm text-cream-100/70 leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SKIN QUIZ */}
      <SkinQuiz products={products} />

      {/* SPECIALTIES BANNER */}
      <section className="relative bg-ink-900 text-cream-100 overflow-hidden" data-testid="home-specialties">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #C9A574 0, transparent 40%), radial-gradient(circle at 80% 80%, #B8945A 0, transparent 45%)" }} aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="eyebrow text-gold-400">{t("specialties_eyebrow", "Why IMMUSKI")}</div>
              <h2 className="mt-3 font-display text-3xl sm:text-5xl leading-[1.05]">
                {t("specialties_title_prefix", "Clinically-led actives.")} <em className="not-italic text-gold-500">{t("specialties_title_accent", "Honestly delivered.")}</em>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-cream-100/70 leading-relaxed text-sm sm:text-base">
                {t("specialties_body", "Four essentials, engineered around the actives dermatologists actually prescribe — PDRN for skin barrier, Redensyl & Anagain for hair density, SPF 50 PA++++ for daily shield, and gentle amino-acid surfactants for cleansing. Every batch is ISO- & GMP-certified, sulphate & paraben-free, and vegan by design.")}
              </p>
            </div>
          </div>

          <div className="mt-12 sm:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-gold-500/20">
            {[
              ["s1", t("specialty_1_num", "50+ PA++++"), t("specialty_1_title", "Broad-spectrum shield"), t("specialty_1_body", "HEV blue-light + PM 2.5 pollution defense in a weightless daily veil.")],
              ["s2", t("specialty_2_num", "PDRN"), t("specialty_2_title", "Barrier recovery serum"), t("specialty_2_body", "Salmon-derived polynucleotides that soothe, repair and strengthen skin.")],
              ["s3", t("specialty_3_num", "Redensyl®"), t("specialty_3_title", "Hair density complex"), t("specialty_3_body", "Redensyl + Anagain + Baicapil — reactivates dormant follicles clinically.")],
              ["s4", t("specialty_4_num", "0 · 0"), t("specialty_4_title", "Sulphates · Parabens"), t("specialty_4_body", "Amino-acid based cleanse; pH-balanced; cruelty-free & vegan by design.")],
            ].map(([key, num, title, body]) => (
              <div key={key} className="bg-ink-900 p-6 sm:p-8 flex flex-col" data-testid={`specialty-${key}`}>
                <div className="font-display text-2xl sm:text-3xl text-gold-500">{num}</div>
                <div className="mt-3 sm:mt-4 text-[15px] sm:text-[17px] text-cream-100 font-medium">{title}</div>
                <p className="mt-2 text-[12px] sm:text-[13px] text-cream-100/60 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 sm:mt-14 flex flex-wrap gap-4">
            <Link to="/shop" className="border border-gold-500 text-cream-100 hover:bg-gold-500 hover:text-ink-900 transition-colors px-6 py-3 text-xs uppercase tracking-[0.28em] inline-flex items-center gap-2" data-testid="specialties-shop-btn">
              {t("specialties_cta_primary", "Explore the essentials")} <ArrowRight size={14} />
            </Link>
            <Link to="/dermatologists" className="text-xs uppercase tracking-[0.28em] text-cream-100/70 hover:text-gold-400 inline-flex items-center gap-2 py-3" data-testid="specialties-derm-btn">
              {t("specialties_cta_secondary", "Or talk to a dermatologist")} →
            </Link>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="eyebrow">{t("story_eyebrow", "Our story")}</div>
          <h2 className="mt-3 font-display text-3xl sm:text-5xl leading-[1.05] text-ink-900">
            {t("story_title_prefix", "Formulated with dermatologists.")} <em className="not-italic text-gold-500">{t("story_title_accent", "Made for daily devotion.")}</em>
          </h2>
          <p className="mt-6 sm:mt-8 text-ink-700 leading-relaxed">
            {t("story_body", "IMMUSKI is a modern apothecary, born in India and shaped by science. Every formula begins in the lab, travels through months of clinical iteration, and lands in your routine as something honest — weightless, effective, and unmistakably kind to skin.")}
          </p>
          <div className="mt-8 sm:mt-10 grid grid-cols-3 gap-4 sm:gap-6 max-w-md">
            <div><div className="font-display text-2xl sm:text-3xl text-gold-500">{t("story_stat_1_num", "7+")}</div><div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-ink-500 mt-1">{t("story_stat_1_label", "Actives per formula")}</div></div>
            <div><div className="font-display text-2xl sm:text-3xl text-gold-500">{t("story_stat_2_num", "36")}</div><div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-ink-500 mt-1">{t("story_stat_2_label", "Months shelf life")}</div></div>
            <div><div className="font-display text-2xl sm:text-3xl text-gold-500">{t("story_stat_3_num", "0")}</div><div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-ink-500 mt-1">{t("story_stat_3_label", "Sulphates · Parabens")}</div></div>
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative aspect-[4/5] bg-cream-200 overflow-hidden">
            {siteImages === null && (
              <div className="absolute inset-0 bg-cream-200 animate-pulse" aria-hidden="true" />
            )}
            {storyImg && (
              <img src={storyImg} alt="Editorial" loading="lazy" decoding="async" className="w-full h-full object-cover" data-testid="home-story-image" />
            )}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y hairline overflow-hidden bg-cream-200">
        <div className="flex gap-8 sm:gap-16 py-4 sm:py-6 whitespace-nowrap animate-marquee font-display text-xl sm:text-3xl text-ink-900">
          {[..."IMMUSKI · Science · Nature · You · The Essentials · IMMUSKI · Science · Nature · You · The Essentials · ".split(" · ")].map((w, i) => (
            <span key={i} className="opacity-80">{w}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
